import React, { useEffect, useRef, useState } from "react";
import { Music, Upload, Trash2, RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Admin tools for the platform jingle (plays every 3 music tracks across every
 *  station) and a one-click "clear ALL cached DJ drops" — used when the DJ system
 *  prompt changes (e.g. adding jokes/news/weather) and old cached drops should be
 *  replaced with freshly-generated banter. */
export default function JingleCard() {
  const [status, setStatus] = useState(null); // { installed, duration, audio_url, version }
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/jingle/status", { headers: authHeaders() });
      setStatus(data);
    } catch (_) {
      setStatus(null);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onPick = () => fileRef.current?.click();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!window.confirm(`Upload "${f.name}" as the platform jingle? It will replace any existing jingle and air every 3 tracks on every station.`)) {
      e.target.value = ""; return;
    }
    setBusy("upload"); setMsg("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/jingle/upload", fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setMsg(`Installed · v${data.version} · ${Math.round(data.duration)}s · ${Math.round(data.size_bytes / 1024)} KB`);
      await refresh();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the platform jingle? No jingle will air across stations until a new one is uploaded.")) return;
    setBusy("delete"); setMsg("");
    try {
      await api.delete("/admin/jingle", { headers: authHeaders() });
      setMsg("Jingle removed.");
      await refresh();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Remove failed");
    }
    setBusy("");
  };

  const clearAllDrops = async () => {
    if (!window.confirm("Wipe ALL cached DJ drops across every station? They'll regenerate fresh (with jokes/news/weather banter) as listeners tune in. Burns a small amount of TTS credits over the next hour.")) return;
    setBusy("clear"); setMsg("");
    try {
      const { data } = await api.post("/admin/dj/clear-all", {}, { headers: authHeaders() });
      setMsg(`Cleared ${data.deleted_drops} cached drops. Fresh banter will air on next play.`);
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Clear failed");
    }
    setBusy("");
  };

  return (
    <section className="mt-10" data-testid="jingle-card">
      <h2 className="fl-display text-2xl font-bold mb-4 flex items-center gap-2">
        <Music className="w-5 h-5 text-emerald-500" /> Platform jingle &amp; DJ banter
      </h2>
      <div className="fl-card p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-[220px] flex-1">
            <div className="text-white font-medium">Fliboe FM station ID</div>
            <div className="text-xs text-slate-400 mt-1">
              {status?.installed ? (
                <>Installed · v{status.version} · {Math.round(status.duration)}s. Plays every 3 music tracks across every station.</>
              ) : (
                <>No jingle installed yet. Upload a short MP3 (≤ 60s) to brand every station with a Fliboe FM ID between tracks.</>
              )}
            </div>
            {status?.installed && (
              <audio
                src={status.audio_url}
                controls
                preload="none"
                className="mt-3 w-full max-w-sm h-10"
                data-testid="jingle-preview-audio"
              />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/aac"
              className="hidden"
              onChange={upload}
              data-testid="jingle-file-input"
            />
            <button
              onClick={onPick}
              disabled={busy === "upload"}
              className="fl-btn"
              data-testid="jingle-upload-btn"
            >
              <Upload className={`w-4 h-4 ${busy === "upload" ? "animate-pulse" : ""}`} />
              {busy === "upload" ? "Uploading…" : status?.installed ? "Replace jingle" : "Upload jingle"}
            </button>
            {status?.installed && (
              <button
                onClick={remove}
                disabled={busy === "delete"}
                className="fl-btn ghost"
                data-testid="jingle-delete-btn"
              >
                <Trash2 className="w-4 h-4 text-rose-400" />
                {busy === "delete" ? "Removing…" : "Remove"}
              </button>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 pt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-[220px] flex-1">
            <div className="text-white font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-fuchsia-400" /> DJ banter (jokes / news / weather)
            </div>
            <div className="text-xs text-slate-400 mt-1">
              The AI DJ now riffs jokes, headlines and weather between tracks. Old cached drops
              still use the previous prompt — clear them so fresh banter airs platform-wide.
            </div>
          </div>
          <div>
            <button
              onClick={clearAllDrops}
              disabled={busy === "clear"}
              className="fl-btn ghost"
              data-testid="dj-clear-all-btn"
            >
              <RefreshCw className={`w-4 h-4 text-fuchsia-400 ${busy === "clear" ? "animate-spin" : ""}`} />
              {busy === "clear" ? "Clearing…" : "Clear all cached drops"}
            </button>
          </div>
        </div>

        {msg && (
          <div className="text-xs text-emerald-400" data-testid="jingle-msg">{msg}</div>
        )}
      </div>
    </section>
  );
}
